/**
 * Search Console formatters (spec §4).
 *
 * Every name states its INPUT UNIT, because the payload mixes two conventions and
 * `shared/format.js` already documents the same trap for `fmtPercent` vs `fmtPctValue`:
 *
 *   - `ctr` and every `*_share` field arrive as a RATIO   (0.0372)  → `fmtRatioPct` / `fmtSharePct`
 *   - every `*_change_pct` field arrives ALREADY-PERCENT  (22.4)    → `fmtSignedPct`
 *
 * Swapping them renders 3.72% as "0.0%" or 22.4% as "2240%" — both look plausible
 * enough to ship, so `format.test.js` asserts both in the same suite.
 *
 * Nothing here derives a number from another number: these convert one value to its
 * display string at the precision the spec asks for (F14 — the payload already carries
 * the intended precision, so the only rounding is the ratio→percent presentation the
 * spec's own §4 table mandates).
 *
 * Every formatter returns `''` for a missing value so callers can omit the element
 * rather than render `undefined` / `NaN` / `null` (F18).
 */

/** True when a value is renderable as a number (rejects null, undefined, '' and NaN). */
const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/** Impressions / clicks / counts: `212840` → `"212,840"`. Never rounded, no decimals. */
export const fmtCount = (v) => (isNum(v) ? Number(v).toLocaleString('en-US') : '');

/** CTR (ratio input): `0.0372` → `"3.7%"`. One decimal. */
export const fmtRatioPct = (v) => (isNum(v) ? `${(Number(v) * 100).toFixed(1)}%` : '');

/** Share (ratio input): `0.5497` → `"55%"`. No decimals. */
export const fmtSharePct = (v) => (isNum(v) ? `${Math.round(Number(v) * 100)}%` : '');

/** Percent change (already-percent input): `22.4` → `"+22.4%"`. Explicit sign. */
export const fmtSignedPct = (v) => {
  if (!isNum(v)) return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
};

/** Average position: `11.23` → `"11.2"`. One decimal, never more. */
export const fmtPosition = (v) => (isNum(v) ? Number(v).toFixed(1) : '');

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 1-based month number: `4` → `"April"`. */
export const fmtMonthNumber = (m) => {
  const n = Number(m);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? MONTH_NAMES[n - 1] : '';
};

/**
 * A `YYYY-MM-DD` date, formatted WITHOUT going through `new Date(iso)`.
 *
 * Search Console dates are Pacific-Time calendar dates, not instants. Parsing them
 * as a Date shifts them a day for viewers west of the payload's timezone, which is
 * exactly the "these don't match the Analytics page" confusion `timezone_note`
 * exists to prevent. Splitting the string keeps the date the backend sent (§4).
 */
export const fmtDate = (iso) => {
  if (typeof iso !== 'string') return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const abbr = MONTH_ABBR[Number(mo) - 1];
  return abbr ? `${abbr} ${Number(d)}, ${y}` : iso;
};

/** A month key: `"2026-06-01"` → `"Jun 2026"`. */
export const fmtMonthKey = (iso) => {
  if (typeof iso !== 'string') return '';
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const abbr = MONTH_ABBR[Number(m[2]) - 1];
  return abbr ? `${abbr} ${m[1]}` : iso;
};

/**
 * Period label from `window.days_with_data` ONLY (F11).
 *
 * `window.start` / `window.end` always span 90 calendar days even on a property with
 * two weeks of data, so labelling from them tells a brand-new owner they are looking
 * at a quarter of history they do not have.
 */
export const windowLabel = (daysWithData) => {
  if (!isNum(daysWithData)) return '';
  const n = Number(daysWithData);
  return n >= 80 ? 'last 90 days' : `${fmtCount(n)} days on record`;
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How far back relative prose stays useful.
 *
 * "2 days ago" is instantly meaningful. "1898 days ago" is a number the reader has to do
 * arithmetic on to place, and a sitemap submitted in 2021 renders exactly that. Past this
 * point the absolute date is the more readable of the two, so the format switches.
 */
const RELATIVE_LIMIT_DAYS = 60;

/**
 * An ISO timestamp as coarse relative prose: `"3 hours ago"`. Timestamps, not dates.
 *
 * Beyond `RELATIVE_LIMIT_DAYS` it returns the date instead — see above. This is
 * formatting, not arithmetic on a payload value (§8): the instant is exactly the one the
 * backend sent, only phrased for a human.
 */
export const fmtRelativeTime = (iso, now = Date.now()) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = now - t;
  if (diff < 0) return '';
  if (diff < HOUR) {
    const mins = Math.floor(diff / MINUTE);
    return mins <= 1 ? 'just now' : `${mins} minutes ago`;
  }
  if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  const days = Math.floor(diff / DAY);
  if (days > RELATIVE_LIMIT_DAYS) return fmtDate(iso);
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

/**
 * One of Google's SCREAMING_SNAKE state values, as readable prose.
 *
 * `INDEXING_ALLOWED` → `Indexing allowed`, `SUCCESSFUL` → `Successful`. These are status
 * names, not identifiers a reader would ever type back into anything, and they reach both
 * a table cell and a spreadsheet column — where an underscore is noise in a report.
 *
 * Deliberately NOT applied to `search_appearance`, whose values name a Google FEATURE
 * ("Review snippets", "Merchant listings") the owner may go looking up. Those stay as
 * Google writes them.
 *
 * @param {unknown} value the raw enum
 * @returns {string} readable prose, or '' when there is nothing to show
 */
export const fmtEnum = (value) => {
  if (typeof value !== 'string' || !value) return '';
  // Only recase values that ARE the shouty form; anything already prose is left alone.
  if (!/^[A-Z0-9_]+$/.test(value)) return value;
  const words = value.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A page URL reduced to its path: `"https://x.com/rv-sites/?a=b"` → `"/rv-sites/?a=b"`. */
export const pagePath = (url) => {
  if (typeof url !== 'string' || !url) return '';
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    return url;
  }
};

/**
 * Words that are shouted rather than capitalised when they appear in a search term.
 *
 * Two-letter state codes that collide with English words are DELIBERATELY absent — `in`,
 * `or`, `co`, `me`, `hi`, `ok`, `id`, `la`, `pa`, `de`, `as`, `oh`, `ma`. "Glamping IN
 * Covered Wagons" is a worse error than "Camp Verde Az", because it happens on ordinary
 * phrases rather than on addresses, and a reader cannot tell it was a rule misfiring.
 *
 * Extend it freely; each entry only ever affects a whole word.
 */
const SHOUTED = new Set([
  // Domain
  'rv', 'rvs', 'atv', 'atvs', 'utv', 'suv', 'koa', 'bbq', 'tv', 'wifi', 'gps', 'hoa', 'ada', 'ev',
  // US state codes with no English-word collision
  'az', 'ak', 'al', 'ar', 'ct', 'fl', 'ga', 'ia', 'il', 'ks', 'ky', 'md', 'mi', 'mn', 'mo',
  'ms', 'mt', 'nc', 'nd', 'ne', 'nh', 'nj', 'nm', 'nv', 'ny', 'ri', 'sc', 'sd', 'tn', 'tx',
  'ut', 'va', 'vt', 'wa', 'wi', 'wv', 'wy', 'usa',
]);

/**
 * A search term, cased for display in a table.
 *
 * Google returns what a person typed, which is lowercase almost always. A column of that
 * reads as raw log output rather than as a report, so each word is capitalised and the
 * acronyms above are shouted: `camp verde az` → `Camp Verde AZ`, `rv parking spot` →
 * `RV Parking Spot`.
 *
 * ── DISPLAY ONLY — never let this value travel ──────────────────────────────────
 * The raw string is what identifies the search everywhere it matters: `p_query` on a row
 * click (which now matches by equality — backend addendum §1, so a single wrong character
 * returns nothing), `p_values` on the sparkline call, and every CSV cell. All of those
 * read `row.query` directly and must keep doing so. This function is for painting a cell
 * and for nothing else.
 *
 * Words already carrying capitals are left alone, so an acronym the backend has already
 * cased correctly is not flattened.
 *
 * NOT used on URLs or on Google's enums (`REVIEW_SNIPPET`, `MOBILE`): a path is
 * case-sensitive and an enum is an identifier, so recasing either invites the reader to
 * retype it wrongly.
 *
 * @param {unknown} term the raw search string
 * @returns {string} the string to paint, or '' when there is nothing to paint
 */
export const fmtSearchTerm = (term) => {
  if (typeof term !== 'string' || !term) return '';
  // Split on the separators rather than on whitespace alone, so the pieces of
  // "camp-verde" and "rv/atv" are each treated as words and the separator survives.
  return term.replace(/[^\s/\-_]+/g, (word) => {
    const lower = word.toLowerCase();
    if (SHOUTED.has(lower)) return word.toUpperCase();
    if (word !== lower) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
};
